/**
 * Client-side keychain for bookings made on this device, per studio.
 * The access token is the client's own secret for exactly one booking; the
 * server only keeps its hash. A pending intent (idempotency key + token) is
 * stored BEFORE the request, so a retry after a dropped connection replays
 * the same request and receives the same booking instead of a duplicate.
 */
import { z } from 'zod';
import { studioUrl } from '@shared/routes';

const entrySchema = z.object({
  id: z.string(),
  token: z.string(),
  code: z.string(),
  startsAt: z.string(),
  serviceName: z.string(),
  savedAt: z.string(),
});
export type StoredBooking = z.infer<typeof entrySchema>;

const pendingSchema = z.object({
  idempotencyKey: z.string(),
  token: z.string(),
  fingerprint: z.string(),
  createdAt: z.string(),
});
export type PendingIntent = z.infer<typeof pendingSchema>;

const key = (slug: string, kind: 'bookings' | 'pending') => `studio:${slug}:${kind}`;

function read<T>(k: string, schema: z.ZodType<T>, fallback: T): T {
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return fallback;
    const parsed = schema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : fallback;
  } catch {
    return fallback;
  }
}

function write(k: string, value: unknown) {
  try {
    localStorage.setItem(k, JSON.stringify(value));
    window.dispatchEvent(new CustomEvent('my-bookings-changed'));
  } catch {
    /* private mode / storage full: booking still succeeds, link is shown on screen */
  }
}

export function listBookings(slug: string): StoredBooking[] {
  return read(key(slug, 'bookings'), z.array(entrySchema), []).sort((a, b) => b.startsAt.localeCompare(a.startsAt));
}

export function findBooking(slug: string, id: string): StoredBooking | undefined {
  return listBookings(slug).find((b) => b.id === id);
}

export function saveBooking(slug: string, entry: Omit<StoredBooking, 'savedAt'>) {
  const rest = listBookings(slug).filter((b) => b.id !== entry.id);
  write(key(slug, 'bookings'), [{ ...entry, savedAt: new Date().toISOString() }, ...rest].slice(0, 30));
}

export function forgetBooking(slug: string, id: string) {
  write(
    key(slug, 'bookings'),
    listBookings(slug).filter((b) => b.id !== id),
  );
}

/** Reuse the pending intent only for the same service+time+contacts. */
export function pendingIntent(slug: string, fingerprint: string, create: () => PendingIntent): PendingIntent {
  const existing = read(key(slug, 'pending'), pendingSchema.nullable(), null);
  if (existing && existing.fingerprint === fingerprint) return existing;
  const fresh = create();
  write(key(slug, 'pending'), fresh);
  return fresh;
}

export function clearPending(slug: string) {
  try {
    localStorage.removeItem(key(slug, 'pending'));
  } catch {
    /* ignore */
  }
}

/** Share link that opens this booking on another device (token in #fragment, never sent to servers). */
export function bookingLink(slug: string, id: string, token: string): string {
  return `${location.origin}${studioUrl.booking(slug, id)}#t=${token}`;
}
